import { MOBILE_SCREEN_QUERY } from "./shared.1cf4cfaf2f6b.js";
import { LocationSelectorChangedEventType } from "./location-selector.588e557db0d7.js";
const DEFAULT_ZOOM = 9;

export const ShowMobileMapEventType = "tq:show_mobile_map";
export const HideMobileMapEventType = "tq:hide_mobile_map";
export const MapMarkerSelectedEventType = "tqc:map_marker:selected";
const numberFormatter = Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  maximumFractionDigits: 0,
});

/**
 * MapWithRates creates a mapbox-gl map with markers representing
 * the price of a given service at a provider, with overlapping
 * features represented as clickable clusters that separate into
 * individual features at the appropriate zoom level.
 *
 * Inputs are embedded as JSON scripts by Django:
 * - mapbox configuration (from <script id="mapbox-config">)
 * - rate data (from <script id="rates-data">)
 */
class MapWithRates {
  constructor() {
    const embeddedJson = this.parseEmbeddedJson();
    this.config = embeddedJson.mapConfig;
    this.rates = embeddedJson.rates;
    this.usePitViperV2 = JSON.parse(
      document.getElementById("use-pit-viper-v2")?.textContent || "false",
    );
    this.state = {
      markers: [],
    };
    this.elements = this.queryDomElements();
    if (!MOBILE_SCREEN_QUERY.matches) {
      this.createAppropriatelySizedMap();
    }
    this.bindEventListeners();
  }

  queryDomElements = () => {
    return {
      mapView: document.getElementById("map-view"),
      mapContainer: document.getElementById("map-container"),
      searchWhenMovingMapCheckbox:
        document.getElementById("search-when-moving"),
      coordinatesContainer: document.getElementById("map-coordinates"),
      latitudeInput: document.querySelector('input[name="search_lat"]'),
      longitudeInput: document.querySelector('input[name="search_lng"]'),
      loaders: document.querySelectorAll(".tqc-map-loader"),
      filtersSection: document.getElementById("filters-section"),
    };
  };

  parseEmbeddedJson = () => {
    const rates = JSON.parse(document.getElementById("rates-data").textContent);
    if (!rates) throw new Error("unable to load embedded rates");

    const mapConfig = JSON.parse(
      document.getElementById("mapbox-config").textContent,
    );
    if (!mapConfig) throw new Error("unable to load page config");
    return { rates, mapConfig };
  };

  calculateDistance(lat1, lng1, lat2, lng2) {
    // Use the Haversine formula to calculate the distance between two points on the Earth's surface
    const R = 6371000; // Earth radius in meters
    const phi1 = (lat1 * Math.PI) / 180;
    const phi2 = (lat2 * Math.PI) / 180;
    const deltaPhi = ((lat2 - lat1) * Math.PI) / 180;
    const deltaLambda = ((lng2 - lng1) * Math.PI) / 180;

    const a =
      Math.sin(deltaPhi / 2) * Math.sin(deltaPhi / 2) +
      Math.cos(phi1) *
        Math.cos(phi2) *
        Math.sin(deltaLambda / 2) *
        Math.sin(deltaLambda / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

    return R * c;
  }

  calculateViewportRadius() {
    const bounds = this.map.getBounds();
    const center = this.map.getCenter();

    // Get distances to the middle of each edge (not corners)
    const north = bounds.getNorth();
    const south = bounds.getSouth();
    const east = bounds.getEast();
    const west = bounds.getWest();

    // Calculate distances from center to each edge midpoint
    const distances = [
      this.calculateDistance(center.lat, center.lng, north, center.lng), // to north edge
      this.calculateDistance(center.lat, center.lng, south, center.lng), // to south edge
      this.calculateDistance(center.lat, center.lng, center.lat, east), // to east edge
      this.calculateDistance(center.lat, center.lng, center.lat, west), // to west edge
    ];

    // Use the MINIMUM distance to ensure results stay within viewport
    const minDistance = Math.min(...distances);

    return Math.floor(minDistance);
  }

  triggerMapSearch() {
    if (!this.elements.searchWhenMovingMapCheckbox.checked) {
      return;
    }

    const { lng, lat } = this.map.getCenter();
    const radiusInMeters = this.calculateViewportRadius();

    // Update form inputs
    this.elements.latitudeInput.value = lat;
    this.elements.longitudeInput.value = lng;

    // Update distance_in_meters input (separate from user's distance selection)
    const distanceInMetersInput = document.querySelector(
      'input[name="distance_in_meters"]',
    );
    if (distanceInMetersInput) {
      distanceInMetersInput.value = radiusInMeters;
    }

    // Trigger the search
    const event = new CustomEvent("map:recenter", {
      bubbles: true,
      detail: {
        lat,
        lng,
        radiusInMeters,
      },
    });
    this.elements.coordinatesContainer.dispatchEvent(event);
  }

  bindEventListeners = () => {
    document.body.addEventListener("htmx:afterRequest", (e) => {
      if (e.detail.target.id == "results-main") {
        const embeddedJson = this.parseEmbeddedJson();
        this.config = embeddedJson.mapConfig;
        this.rates = embeddedJson.rates;
        this.rebuildMapSource();
        this.elements = this.queryDomElements();
      }
    });

    document.body.addEventListener("htmx:afterSettle", () => {
      this.hideLoader();
    });

    document.addEventListener(ShowMobileMapEventType, () => {
      this.showMobileMap();
    });

    document.addEventListener(HideMobileMapEventType, () => {
      this.hideMobileMap();
    });

    // recreate the map if we back-navigated
    window.addEventListener("popstate", () => {
      // map not yet initialized
      if (!window.mapboxgl) {
        return;
      }

      this.createMap();
    });
  };

  /**
   * Provides rate data to the mapbox Map as a GeoJSON data source,
   * so that we can derive our custom HTML markers.
   *
   * This happens every time we get new rates, whether they come from
   * page load or an htmx search.
   */
  rebuildMapSource() {
    if (!this.map) return;

    if (this.map.getSource("rates")) {
      if (this.map.getLayer("hidden-clusters")) {
        this.map.removeLayer("hidden-clusters");
      }
      this.map.removeSource("rates");
    }

    const geojson = {
      type: "FeatureCollection",
      features: this.rates.map((rate) => ({
        type: "Feature",
        geometry: {
          type: "Point",
          coordinates: [rate.longitude, rate.latitude],
        },
        properties: {
          cardIndex: rate.card_index,
          price: rate.display_price,
          url: rate.cost_breakdown_url,
          provider: rate.provider_name,
          city: rate.city,
          state: rate.state,
          zip: rate.zip_code,
          providerType: rate.provider_type_display,
        },
      })),
    };

    /**
     * Stores the geojson data on the mapbox instance for later access
     * via Map#querySourceFeatures
     *
     * https://docs.mapbox.com/mapbox-gl-js/api/map/#map#querysourcefeatures
     */
    this.map.addSource("rates", {
      type: "geojson",
      data: geojson,
      cluster: true,
      clusterMaxZoom: 14,
      clusterRadius: 40, // make this larger if unclustered points are bumping together
    });

    // querySourceFeatures could not find anything unless a layer was present
    this.map.addLayer({
      id: "hidden-clusters",
      type: "circle",
      source: "rates",
      filter: ["has", "point_count"],
      paint: { "circle-opacity": 0 },
    });

    /**
     * When we rebuild the GeoJSON data and add the layer to mapbox,
     * we need to redraw the markers.
     */
    this.map.once("idle", this.makeMarkers);
  }

  /**
   * Adds map markers representing a count of overlapping providers at a given point.
   *
   * Clicking the counter zooms in, and once the map settles (and fires `idle`)
   * markers will be redrawn.
   */
  createClusterMarker = (feature) => {
    const clusterData = feature.properties;
    const markerWrapper = document.createElement("div");
    markerWrapper.className =
      "map-marker pv-label-selectable-small pv-max-content pv-shadow pv-flex-vertical";
    markerWrapper.style =
      "border-radius: 9999px; width: 30px; height: 30px; --flex-align: center; --flex-justify: center; --selectable-label-hover-background-color: white;";
    markerWrapper.dataset.clusterId = clusterData.cluster_id;
    markerWrapper.dataset.coords = JSON.stringify(feature.geometry.coordinates);

    const markerContent = document.createElement("div");
    markerContent.className = "pv-text-title-xs tqc-text-interactive";
    markerContent.style = "--line-height: 1";
    markerContent.textContent = clusterData.point_count_abbreviated;
    markerWrapper.appendChild(markerContent);

    const marker = new mapboxgl.Marker({
      element: markerWrapper,
    })
      .setLngLat(feature.geometry.coordinates)
      .addTo(this.map);

    this.state.markers.push(marker);

    markerWrapper.addEventListener("click", (e) => {
      const wrapperData = e.currentTarget.dataset;
      const clusterId = parseInt(wrapperData.clusterId);
      this.map
        .getSource("rates")
        .getClusterExpansionZoom(clusterId, (err, zoom) => {
          if (err) return;
          this.map.easeTo({
            center: JSON.parse(wrapperData.coords),
            zoom,
          });
        });
    });
  };

  /**
   * Adds map markers for rates that do not overlap / are not rolled up in a cluster.
   */
  createSinglePointMarker = (feature) => {
    const rate = feature.properties;
    const markerContent = document.createElement("span");
    const markerWrapper = document.createElement("span");
    markerWrapper.dataset.cardIndex = rate.cardIndex;
    markerWrapper.className =
      "map-marker pv-label-selectable-small pv-max-content pv-shadow";
    markerWrapper.style = "--selectable-label-hover-background-color: white;";
    markerContent.className = "pv-text-title-xs tqc-text-interactive";
    markerContent.textContent = numberFormatter.format(rate.price);
    markerWrapper.appendChild(markerContent);

    const targetForResultsPage = MOBILE_SCREEN_QUERY.matches
      ? "_self"
      : "_blank";

    const marker = new mapboxgl.Marker({
      element: markerWrapper,
    })
      .setLngLat(feature.geometry.coordinates)
      .setPopup(
        new mapboxgl.Popup({
          className: "pv-shadow pv-bordered-lg pv-inset-square-12 pv-surface",
          closeButton: true,
        }).setHTML(`
          ${rate.url ? `<a class="map-marker-link" href="${rate.url}" target="${targetForResultsPage}" data-cardIndex="${rate.cardIndex}">` : "<div>"}
            <div>
              <p class="pv-text-title-sm tqc-text-interactive" translate="no">${rate.provider}</p>
              <p class="pv-text-body-sm pv-text-default" translate="no">${rate.city}, ${rate.state} ${rate.zip}</p>
              <p class="pv-text-subdued">${rate.providerType}</p>
            </div>
          ${rate.url ? "</a>" : "</div>"}
        `),
      )
      .addTo(this.map);

    this.state.markers.push(marker);

    markerWrapper.addEventListener("click", () => {
      const event = new CustomEvent(MapMarkerSelectedEventType, {
        bubbles: true,
        detail: { cardIndex: rate.cardIndex },
      });
      document.dispatchEvent(event);
    });
  };

  createClusterMarkerV2 = (feature) => {
    const clusterData = feature.properties;
    const markerWrapper = document.createElement("div");
    markerWrapper.className =
      "tqc-map-marker-v2 tqc-map-marker-v2--cluster pv-flex pv-inset-square pv-radius";
    markerWrapper.dataset.clusterId = clusterData.cluster_id;
    markerWrapper.dataset.coords = JSON.stringify(feature.geometry.coordinates);

    const markerContent = document.createElement("span");
    markerContent.className =
      "tqc-map-marker-v2__text pv-text-inverse pv-text-body-sm";
    markerContent.textContent = clusterData.point_count_abbreviated;
    markerWrapper.appendChild(markerContent);

    const marker = new mapboxgl.Marker({
      element: markerWrapper,
    })
      .setLngLat(feature.geometry.coordinates)
      .addTo(this.map);

    this.state.markers.push(marker);

    markerWrapper.addEventListener("click", (e) => {
      const wrapperData = e.currentTarget.dataset;
      const clusterId = parseInt(wrapperData.clusterId);
      this.map
        .getSource("rates")
        .getClusterExpansionZoom(clusterId, (err, zoom) => {
          if (err) return;
          this.map.easeTo({
            center: JSON.parse(wrapperData.coords),
            zoom,
          });
        });
    });
  };

  createSinglePointMarkerV2 = (feature) => {
    const rate = feature.properties;
    const markerWrapper = document.createElement("div");
    markerWrapper.dataset.cardIndex = rate.cardIndex;
    markerWrapper.className =
      "tqc-map-marker-v2 pv-flex pv-inset-squish pv-radius";

    const markerContent = document.createElement("span");
    markerContent.className =
      "tqc-map-marker-v2__text pv-text-inverse pv-text-body-sm";
    markerContent.textContent = numberFormatter.format(rate.price);
    markerWrapper.appendChild(markerContent);

    const targetForResultsPage = MOBILE_SCREEN_QUERY.matches
      ? "_self"
      : "_blank";

    const marker = new mapboxgl.Marker({
      element: markerWrapper,
    })
      .setLngLat(feature.geometry.coordinates)
      .setPopup(
        new mapboxgl.Popup({
          className:
            "pv-bordered-md pv-inset-square-12 pv-surface tqc-map-popup-v2",
          closeButton: true,
          maxWidth: "280px",
        }).setHTML(`
          ${rate.url ? `<a class="map-marker-link" href="${rate.url}" target="${targetForResultsPage}" data-cardIndex="${rate.cardIndex}">` : "<div>"}
            <div class="pv-flex-vertical" style="--flex-gap: 4px; --flex-align: start;">
              <p class="pv-text-body-sm tqc-text-interactive" style="font-weight: 500;" translate="no">${rate.provider}</p>
              <p class="pv-text-body-md pv-text-default" translate="no">${rate.city}, ${rate.state} ${rate.zip}</p>
              <p class="pv-text-body-sm pv-text-subdued" style="font-weight: 500;">${rate.providerType}</p>
            </div>
          ${rate.url ? "</a>" : "</div>"}
        `),
      )
      .addTo(this.map);

    this.state.markers.push(marker);

    markerWrapper.addEventListener("click", () => {
      const event = new CustomEvent(MapMarkerSelectedEventType, {
        bubbles: true,
        detail: { cardIndex: rate.cardIndex },
      });
      document.dispatchEvent(event);
    });
  };

  makeMarkers = () => {
    this.state.markers.forEach((m) => {
      m.remove();
    });
    this.state.markers = [];

    // Features in a Mapbox map's GeoJSON source can be filtered using
    // Expressions, specified here:
    // https://docs.mapbox.com/style-spec/reference/expressions/
    const clustersOnlyFilter = ["has", "point_count"];
    const clusterFeatures = this.map.querySourceFeatures("rates", {
      filter: clustersOnlyFilter,
    });

    const singlePointsOnlyfilter = ["!", clustersOnlyFilter];
    const singlePointFeatures = this.map.querySourceFeatures("rates", {
      filter: singlePointsOnlyfilter,
    });

    if (this.usePitViperV2) {
      clusterFeatures.forEach(this.createClusterMarkerV2);
      singlePointFeatures.forEach(this.createSinglePointMarkerV2);
    } else {
      clusterFeatures.forEach(this.createClusterMarker);
      singlePointFeatures.forEach(this.createSinglePointMarker);
    }
  };

  createMapOptions() {
    const mapOptions = {
      container: "tqc-mapbox",
      style: "mapbox://styles/mapbox/streets-v12", // style URL
      center: this.config.mapCenter,
      zoom: DEFAULT_ZOOM, // starting zoom
    };
    if (this.rates.length > 0) {
      // If we have rates, center the map at the geo center
      // of the map markers
      const mapMarkerBounds = this.rates.reduce((bounds, rate) => {
        return bounds.extend([rate.longitude, rate.latitude]);
      }, new mapboxgl.LngLatBounds());
      mapOptions.bounds = mapMarkerBounds;
      mapOptions.center = mapMarkerBounds.getCenter();
      mapOptions.fitBoundsOptions = {
        padding: 36,
        maxZoom: DEFAULT_ZOOM,
      };
      mapOptions.zoom = null; // starting zoom will be set by map marker center
    }
    return mapOptions;
  }

  createMap() {
    if (this.map && this.map.remove) {
      this.map.remove();
    }

    mapboxgl.accessToken = this.config.mapboxToken;
    const map = new mapboxgl.Map(this.createMapOptions());
    map.on("load", () => {
      this.rebuildMapSource();
    });
    this.map = map;

    // Drags and zooms trigger new searches.
    map.on("dragend", () => {
      this.triggerMapSearch();
    });
    map.on("zoomend", (e) => {
      if (!e.programmaticZoom) {
        this.triggerMapSearch();
      }
    });

    // `moveend` makes new markers, since the clusterization may have changed.
    map.on("moveend", () => {
      this.map.once("idle", () => {
        this.makeMarkers();
      });
    });

    document.addEventListener(LocationSelectorChangedEventType, (e) => {
      const { lat, lng } = e.detail;
      this.elements.latitudeInput.value = lat;
      this.elements.longitudeInput.value = lng;
      map.setZoom(DEFAULT_ZOOM, { programmaticZoom: true });
      map.setCenter([lng, lat]);
      this.rebuildMapSource();
    });
  }

  loadMapboxJs() {
    return new Promise((resolve, reject) => {
      const script = document.createElement("script");
      script.src = this.config.mapboxJsUrl;
      script.onload = resolve;
      script.onerror = reject;
      document.head.appendChild(script);
    });
  }

  loadMapboxCss() {
    return new Promise((resolve, reject) => {
      const link = document.createElement("link");
      link.rel = "stylesheet";
      link.href = this.config.mapboxCssUrl;
      link.onload = resolve;
      link.onerror = reject;
      document.head.appendChild(link);
    });
  }

  async loadMapboxAssets() {
    await Promise.all([this.loadMapboxCss(), this.loadMapboxJs()]);

    this.hideLoader();
  }

  /**
   * We need to stay in sync with screen resizes, to support
   * landscape->portrait rotations and resizing the window.
   */
  async createAppropriatelySizedMap() {
    await this.loadMapboxAssets();

    if (!this.map) {
      this.createMap();
    } else {
      this.map.resize();
    }
  }

  hideLoader() {
    this.elements.loaders.forEach((loader) => {
      loader.classList.remove("tqc-map-loader--show");
    });
  }

  showMobileMap = () => {
    this.elements.mapContainer.classList.add("tqc-mobile-map-active");
    this.createAppropriatelySizedMap();
  };

  hideMobileMap = () => {
    this.elements.mapContainer.classList.remove("tqc-mobile-map-active");
  };
}

document.addEventListener("DOMContentLoaded", () => {
  const hasMapContainer = document.getElementById("map-view");

  if (!hasMapContainer) return;

  if (!document.getElementById("mapbox-config")) {
    throw new Error(
      "Map container exists but mapbox-config is missing. This indicates a template bug.",
    );
  }

  new MapWithRates();
});
